/** @format */
import {
    CourseUnit,
    CreditRange,
    CustomCourseUnitAttainment,
    CustomStudyDraft,
    LocalDateRange,
    LocalDateString,
    LocalizedString,
    Module,
    OtmId,
    StudyPeriodLocator,
    TimingTemplate,
} from 'common-typescript/types';

export interface StudyTermData {
    readonly attainedCredits: number;
    readonly name: LocalizedString;
    readonly startYear: number;
    readonly targetCredits?: number;
    /** Expiry date and id of expired study right, if study right is expired on this term */
    readonly studyRightExpiredInfo?: StudyRightExpiredInfo;
    readonly timedCredits: CreditRange;
    readonly valid: LocalDateRange;
    readonly periods: readonly StudyPeriodData[];
}

export interface StudyPeriodData {
    readonly locator: StudyPeriodLocator;
    readonly name: LocalizedString;
    readonly timelineItems: readonly TimelineItem[];
    readonly valid: LocalDateRange;
    readonly attainedCredits: number;
    readonly timedCredits: CreditRange;
}

export interface StudyRightExpiredInfo {
    readonly studyRightExpiredDateInclusive: LocalDateString;
    readonly studyRightId: OtmId;
}

export interface TimingTemplateSelection {
    /**
     * A module under which untimed course units are sought.
     */
    readonly module: Module;

    /**
     * A timing template belonging to {@link module}.
     */
    readonly timingTemplate: TimingTemplate;
}

/** Controls for the timeline view, persisted in session storage. */
export interface TimelineControls {
    /** Whether to show empty summer periods in the timeline. */
    readonly showEmptySummerPeriods: boolean;
    /** Whether to show past study terms in the timeline. */
    readonly showPastStudyTerms: boolean;
    /** Whether to use horizontal layout for the timeline. */
    readonly useHorizontalLayout: boolean;
}

export interface TimelineItem {
    /** Each row must have a unique id within the containing study period. */
    readonly id: string;
    readonly type: 'CourseUnit' | 'CustomCourseUnitAttainment' | 'CustomStudyDraft';
}

/**
 * A course unit that has been timed to the containing period
 * or attained during it.
 */
export interface TimelineCourseUnit extends TimelineItem {
    readonly type: 'CourseUnit';
    readonly courseUnit: CourseUnit;
}

/**
 * A custom course unit attainment taking place during the containing period.
 */
export interface TimelineCustomCourseUnitAttainment extends TimelineItem {
    readonly type: 'CustomCourseUnitAttainment';
    readonly customCourseUnitAttainment: CustomCourseUnitAttainment;
}

/**
 * A custom study draft that has been timed to the containing period.
 */
export interface TimelineCustomStudyDraft extends TimelineItem {
    readonly type: 'CustomStudyDraft';
    readonly customStudyDraft: CustomStudyDraft;
}

export function isTimelineCourseUnit(item: TimelineItem): item is TimelineCourseUnit {
    return item?.type === 'CourseUnit';
}

export function isTimelineCustomCourseUnitAttainment(item: TimelineItem): item is TimelineCustomCourseUnitAttainment {
    return item?.type === 'CustomCourseUnitAttainment';
}

export function isTimelineCustomStudyDraft(item: TimelineItem): item is TimelineCustomStudyDraft {
    return item?.type === 'CustomStudyDraft';
}
